import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { getTenantSlug } from '../lib/tenant';
import { getFeatures, type FeatureKey } from '../lib/featureFlags';
import type { FocalPoint } from '../components/course-builder/slides/FocalPointPicker';
import type { BrandSlideTheme } from '../components/course-builder/slides/slideTypes';
import type { CatalogBrandingConfig } from '../lib/catalogGrouping';

export interface MarketingHero {
  headline?: string;
  subtitle?: string;
  ctaText?: string;
  ctaHref?: string;
  imageUrl?: string;
  imagePosition?: FocalPoint;
  /** Optional small label above the headline. Empty/undefined = no chip. */
  eyebrow?: string;

  /* Revise-only additions. The revise hero carries four distinct registers —
   * the claim the headline answers, the headline, the generosity argument and
   * the credential — plus a row of product captures rather than one image. */
  /** Sentence above the headline, in the body face. Supports `*accent*`. */
  claim?: string;
  /** The generosity argument under the CTA. Body weight, not fine print. */
  reassure?: string;
  /** One-line credential under that. Quieter than `reassure`. */
  credit?: string;
  /** Product captures beside/below the hero copy. */
  shots?: MarketingShot[];
}

export interface MarketingValueProp {
  icon?: string;
  title?: string;
  body?: string;
}

export interface MarketingCTA {
  headline?: string;
  body?: string;
  buttonText?: string;
  /** Optional small label above the CTA headline. Empty/undefined = no chip. */
  eyebrow?: string;
  /** Where the closing button goes. Falls back to the hero's `ctaHref` when
   *  absent, which is the old behaviour: the same door twice. Set it when the
   *  two ends of the page should differ — revise sends the hero into the
   *  on-page demo and the closing band out to the catalogue. */
  ctaHref?: string;
}

/** Optional heading-level overrides for sections that otherwise hard-code their copy. */
export interface MarketingCopyOverrides {
  valuePropsHeading?: string;
  valuePropsSubhead?: string;
  outcomesHeading?: string;
  outcomesSubhead?: string;
  instructorHeading?: string;
  instructorSubhead?: string;
  testimonialsHeading?: string;
  testimonialsSubhead?: string;
}

export type MarketingThemeId = 'editorial' | 'warm' | 'utility' | 'revise';

export const DEFAULT_MARKETING_THEME: MarketingThemeId = 'editorial';

/**
 * Themes that are deliberately NOT offered to every tenant. `revise` was drawn
 * to match the interactive webapps inside the Revise tenant, so it is bespoke
 * by intent rather than by accident — it reads nothing like the rest of the
 * LMS and would be the wrong default for anyone else. The admin theme picker
 * shows it only to the tenants listed here; the renderer honours it wherever
 * it is already set, so nothing breaks if a slug changes.
 */
export const THEME_TENANT_ALLOWLIST: Partial<Record<MarketingThemeId, readonly string[]>> = {
  revise: ['revise'],
};

/** Whether a tenant slug may SELECT this theme in admin. */
export const isThemeSelectableBy = (theme: MarketingThemeId, slug: string | undefined): boolean => {
  const allowed = THEME_TENANT_ALLOWLIST[theme];
  return !allowed || (!!slug && allowed.includes(slug));
};

/** Default render order for each theme. Used by the renderer fallback and
 *  by the admin page-composition editor. Keep in sync with MarketingSectionId. */
export const THEME_SECTIONS_DEFAULT: Record<MarketingThemeId, ReadonlyArray<MarketingSectionId>> = {
  editorial: ['hero', 'outcomes', 'valueProps', 'courses', 'instructor', 'testimonials', 'cta'],
  warm:      ['hero', 'belief', 'steps', 'courses', 'instructor', 'testimonials', 'cta'],
  utility:   ['hero', 'metrics', 'matrix', 'pricing', 'instructor', 'testimonials', 'faq', 'cta'],
  // The three lab sections are separate IDs rather than one repeated block:
  // the renderer keys section components by ID with no props, so three
  // instances of one block cannot be told apart.
  //
  // `outcomes` and `gifts` are deliberately absent (v4). The six-panel report
  // gallery became a switcher inside labEssay, and the "what's free" section
  // folded into labPractice as ruled notes — the giveaway reads better as part
  // of the product it belongs to than as a separate pitch. Both renderers stay
  // registered so a tenant can still put them back.
  // Two windows into the catalogue, at the two moments someone asks a
  // different question. `bundles` sits straight after the hero — what is on
  // offer, before the three sections that argue for it. `courses` sits before
  // pricing — what you actually get, right where the cost is named.
  revise:    ['hero', 'bundles', 'labStudy', 'labEssay', 'labPractice', 'courses', 'pricing', 'credits', 'cta'],
};

/** Single source of truth for the per-theme section list, with editorial
 *  fallback when an unknown theme slips through. */
export const getThemeDefaultSections = (
  theme: MarketingThemeId,
): ReadonlyArray<MarketingSectionId> =>
  THEME_SECTIONS_DEFAULT[theme] ?? THEME_SECTIONS_DEFAULT[DEFAULT_MARKETING_THEME];

/** Human-readable labels per section ID — used by the admin composition
 *  editor and any future surface that displays section names. */
export const MARKETING_SECTION_LABELS: Record<MarketingSectionId, string> = {
  hero: 'Hero',
  belief: 'Belief statement',
  steps: 'How it works steps',
  metrics: 'Metrics',
  matrix: 'Curriculum matrix',
  outcomes: 'Outcomes',
  valueProps: 'Value props',
  courses: 'Featured courses',
  instructor: 'Instructor spotlight',
  testimonials: 'Testimonials',
  pricing: 'Pricing',
  faq: 'FAQ',
  labStudy: 'Study Labs section',
  labEssay: 'Essay Labs section',
  labPractice: 'Practice Labs section',
  bundles: 'Bundles window',
  gifts: "What's free",
  credits: 'Who made this',
  cta: 'Call to action',
};

/** All section IDs in canonical order (used by the admin "add section" picker). */
export const ALL_MARKETING_SECTION_IDS: ReadonlyArray<MarketingSectionId> =
  Object.keys(MARKETING_SECTION_LABELS) as MarketingSectionId[];

/** Section IDs in the order the page should render. Renderer skips IDs whose content is empty. */
export type MarketingSectionId =
  | 'hero'
  | 'belief'
  | 'steps'
  | 'metrics'
  | 'matrix'
  | 'pricing'
  | 'faq'
  | 'outcomes'
  | 'valueProps'
  | 'courses'
  | 'instructor'
  | 'testimonials'
  | 'labStudy'
  | 'labEssay'
  | 'labPractice'
  | 'bundles'
  | 'gifts'
  | 'credits'
  | 'cta';

/** Warm-only: single-statement band ("What we believe"). */
export interface MarketingBelief {
  eyebrow?: string;
  statement: string;
}

/** Warm-only: numbered "How it works" steps. */
export interface MarketingStep {
  title: string;
  body: string;
}

/** A single learner outcome — verb-led title plus optional elaboration. */
export interface MarketingOutcome {
  title: string;
  body?: string;
  /** Optional capture above the title. Themes that don't render images ignore
   *  it, so this stays additive for editorial/warm/utility. */
  shot?: MarketingShot;
}

/**
 * Single-instructor spotlight.
 * Either reference a real user (preferred) or supply inline overrides.
 * Inline fields always win when both are set.
 */
export interface MarketingInstructor {
  userId?: string;
  name?: string;
  role?: string;
  imageUrl?: string;
  imagePosition?: FocalPoint;
  bio?: string;
  pullQuote?: string;
}

export interface MarketingTestimonial {
  quote: string;
  authorName: string;
  authorRole?: string;
  authorImageUrl?: string;
}

/**
 * A product capture. `width`/`height` are the image's NATIVE pixel dimensions
 * and are load-bearing, not decoration:
 *
 *  - They set the intrinsic aspect ratio, so a shot reserves its space before
 *    it loads and nothing on the page jumps.
 *  - The renderer caps display width at `width / density`, so a capture is
 *    never drawn larger than the resolution it was taken at. Upscaling a UI
 *    screenshot is what makes it look blurry, and a wide column should
 *    produce more margin, never a bigger picture.
 */
export interface MarketingShot {
  src: string;
  alt: string;
  /** Small label on the frame's title bar, e.g. "Study Lab". */
  label?: string;
  /** Native pixel width of the source image. */
  width: number;
  /** Native pixel height of the source image. */
  height: number;
  /** Capture density. 2 = a 2x retina capture, so it caps at width/2 CSS px. */
  density?: 1 | 2;
}

/** One panel of a tabbed capture switcher. */
export interface MarketingShotPanel extends MarketingShot {
  /** Tab label. */
  tab: string;
  /** Sentence shown under the frame while this panel is active. */
  caption: string;
}

/** Revise-only: one row inside a lab section — copy beside a capture. */
export interface MarketingLabBeat {
  title?: string;
  /** Body paragraphs, in order. */
  body?: string[];
  /** Pull-quote rendered between the first and second paragraph. */
  quote?: string;
  /** A single capture, OR `switcher` for a tabbed set. `switcher` wins. */
  shot?: MarketingShot;
  switcher?: MarketingShotPanel[];
  /** Accessible label for the switcher's tab group. */
  switcherLabel?: string;
  /** Render full-bleed with the capture below the copy, not beside it. Use
   *  when a capture is too wide to stay legible in a side column. */
  wide?: boolean;
}

/** Revise-only: a single capture presented on its own, with a caption under it
 *  rather than beside it. Used where the picture is the argument. */
export interface MarketingLabSolo {
  shot: MarketingShot;
  caption?: string;
}

/** Revise-only: a ruled note under a section. `label` renders as a kicker above
 *  it — used for "Free for everybody, always". */
export interface MarketingLabNote {
  label?: string;
  /** Supports `*emphasis*`. Optional: a note may be a label on its own, which
   *  is how Practice Labs states the giveaway now — the paragraph under it
   *  argued a case the section already makes. */
  text?: string;
  /** Which side of the section's figures this sits on. Defaults to 'after'.
   *  Practice Labs states the giveaway before the captures and the teacher
   *  note after them, so one list has to be able to straddle. */
  position?: 'before' | 'after';
}

/** Revise-only: the try-it-here block. Its body is the pitch; the interactive
 *  itself is a component, not content. */
export interface MarketingLabDemo {
  /** Sits in the lesson card's own header bar, not on a wrapper around it. */
  tag?: string;
  /** Optional heading above the card. Off by default — the section head and
   *  the hero have already said what this is. */
  title?: string;
  /** One line above the card. Earn it: say something the section head and the
   *  hero have not already said. */
  body?: string;
}

/** Revise-only: the button that closes a lab section.
 *
 *  Deliberately worded away from the hero's "Try a lesson free": that one opens
 *  the demo on this page, this one leaves for the real thing. `note` is the
 *  quiet line under it, not fine print. */
export interface MarketingLabCta {
  label?: string;
  href?: string;
  note?: string;
}

/** Revise-only: a product section (Study / Essay / Practice Labs). */
export interface MarketingLab {
  eyebrow?: string;
  /** Supports `*accent*` for the accent-coloured clause. */
  heading?: string;
  lede?: string;
  /** The try-a-lesson block. Only one lab should carry it. */
  demo?: MarketingLabDemo;
  /** A lone full-width capture with a caption, above any beats. */
  solo?: MarketingLabSolo;
  beats?: MarketingLabBeat[];
  /** A tabbed capture set belonging to the section itself rather than to a
   *  beat. v4 uses this for Essay ("what comes back") and Practice, where the
   *  captures ARE the argument and need no copy beside them. */
  switcher?: MarketingShotPanel[];
  switcherLabel?: string;
  /** Ruled notes under the beats — the Practice Labs giveaway statements. */
  notes?: MarketingLabNote[];
  /** Optional catalogue table under the beats. Reuses the matrix row shape. */
  topics?: MarketingMatrixCourse[];
  /** When set, the topics table collapses behind a disclosure with this label.
   *  Twelve rows is a wall; six was a list. */
  topicsSummary?: string;
  /** A button closing the section. Only Practice Labs carries one — it is the
   *  only section whose product you can reach without paying. */
  cta?: MarketingLabCta;
}

/** Revise-only: the browse window — a slice of `/courses` on the landing page,
 *  rendered with the catalogue's own card so it reads as a window into that
 *  page rather than a second design of the same thing. */
export interface MarketingBrowse {
  eyebrow?: string;
  /** Supports `*accent*` for the accent-coloured clause. */
  heading?: string;
  lede?: string;
  ctaLabel?: string;
  ctaHref?: string;
}

/** Revise-only: one "what's free" card. */
export interface MarketingGift {
  tag?: string;
  title: string;
  body: string[];
  /** Closing line in the accent colour — who it is for. */
  who?: string;
}

/** Revise-only: the "what's free" section. */
export interface MarketingGifts {
  eyebrow?: string;
  heading?: string;
  lede?: string;
  items: MarketingGift[];
  /** Ruled note under the cards. Supports `*accent*`. */
  foot?: string;
}

/** Revise-only: "who made this" — prose, not a person card. */
export interface MarketingCredits {
  eyebrow?: string;
  heading?: string;
  paragraphs: string[];
}

/** Revise-only: the sticky bar above the nav. Hidden when `text` is empty. */
export interface MarketingAnnounce {
  badge?: string;
  text?: string;
  /** In-page anchor or route. Defaults to the gifts section. */
  href?: string;
}

export interface MarketingContent {
  /** Visual theme. Defaults to 'editorial' when absent (post-migration this is set explicitly). */
  theme?: MarketingThemeId;
  /** Explicit section ordering. When absent the renderer uses the theme default. */
  sections?: MarketingSectionId[];

  hero?: MarketingHero;
  valueProps?: MarketingValueProp[];
  cta?: MarketingCTA;
  copy?: MarketingCopyOverrides;

  outcomes?: MarketingOutcome[];
  instructor?: MarketingInstructor;
  testimonials?: MarketingTestimonial[];
  belief?: MarketingBelief;
  steps?: MarketingStep[];
  metrics?: MarketingMetric[];
  matrix?: MarketingMatrixCourse[];
  pricing?: MarketingPricing;
  faq?: MarketingFaqItem[];

  /* Revise-only blocks. Keyed by lab so the three sections share one renderer
   * and one shape; the section IDs (labStudy/labEssay/labPractice) select which
   * entry each renders. */
  labs?: Partial<Record<MarketingLabKey, MarketingLab>>;
  browse?: MarketingBrowse;
  /** The bundles window. Same shape as `browse` — it is the same block with a
   *  different shelf behind it. */
  bundles?: MarketingBrowse;
  gifts?: MarketingGifts;
  credits?: MarketingCredits;
  announce?: MarketingAnnounce;
  /** Footer provenance statements — what is departmental material and what is
   *  the tenant's own. Rendered as separate paragraphs. */
  legal?: string[];
  /** Footer links beside Privacy/Terms — e.g. a returns policy hosted off-site.
   *  An absolute URL opens in a new tab; a path routes in-app. */
  footerLinks?: Array<{ label: string; href: string }>;
}

/** The three revise product lines. Each maps to one section ID and one accent. */
export type MarketingLabKey = 'study' | 'essay' | 'practice';

/** Utility-only: hard-numbers metrics strip. */
export interface MarketingMetric {
  label: string;
  value: string;
  delta?: string;
  intent?: 'go' | 'warn' | 'info';
}

/** Utility-only: spec-sheet curriculum row. Free-form so any track/level
 * vocabulary works. Tenants who don't want to maintain this can leave it
 * empty and FeaturedCourses still renders. */
export interface MarketingMatrixCourse {
  id: string;        // e.g. "DS-204"
  title: string;
  subtitle?: string;
  level?: number;    // 1–4 dots
  track?: string;
  duration?: string;
  price?: string;
}

/** Utility-only: 1, 2, or 3 pricing plans. Free-form price strings —
 * different tenants price wildly differently. */
export interface MarketingPricing {
  heading?: string;
  subhead?: string;
  plans: MarketingPlan[];
}
export interface MarketingPlan {
  name: string;
  tagline: string;
  price: string;
  pricePer?: string;
  features: string[];
  ctaLabel: string;
  ctaHref: string;
  featured?: boolean;
  /** Optional grouping key. Plans sharing a key render as one row; groups
   *  render in first-appearance order. Absent = one row of everything, which
   *  is what the utility theme has always done. */
  group?: string;
  /** Small flag under the plan, e.g. "Launch pricing for 2026". */
  badge?: string;
  /** Visible label for this plan's row ("One at a time", "By the paper").
   *  Taken from the first plan in each group; later ones are ignored. */
  groupLabel?: string;
}

/** Utility-only: FAQ accordion items. */
export interface MarketingFaqItem {
  question: string;
  answer: string;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  /**
   * Optional logo variant for light backgrounds. Use via `pickTenantLogo(tenant, 'light')`
   * (or fall back inline: `tenant.logoUrlLight || tenant.logoUrl`). Set when the primary
   * `logoUrl` is reversed-out / white and would be illegible on the light student shell.
   */
  logoUrlLight: string | null;
  isPublished: boolean;
  branding: {
    primaryColor?: string;
    secondaryColor?: string;
    primaryColorDark?: string;
    secondaryColorDark?: string;
    loginPrimaryColor?: string;
    loginPrimaryColorDark?: string;
    loginTheme?: 'light' | 'dark';
    fontFamily?: string;
    loginFontFamily?: string;
    loginHeadline?: string;
    loginSubtitle?: string;
    schoolName?: string;
    loginImageUrl?: string;
    loginImagePosition?: { x: number; y: number };
    marketing?: MarketingContent;
    slideTheme?: BrandSlideTheme;
    /**
     * Chrome treatment for the SIGNED-IN app shell (AppLayout's navbar and
     * footer). `'dark'` paints them deep blue; page bodies stay light either
     * way. Default `'light'`.
     *
     * Deliberately its own field rather than being derived from
     * `marketing.theme`: the marketing site and the signed-in product are
     * separate systems, and a tenant may want one without the other. It is also
     * NOT dark mode — the student shell stays light-only (see the Design System
     * notes in CLAUDE.md); this is chrome, so the nav and footer can carry a
     * tenant's identity across the sign-in boundary without restyling content.
     */
    appChrome?: 'light' | 'dark';
    /**
     * Grouped-catalogue configuration: names the tag category whose tags become
     * the catalogue's shelves (and optionally a second that sub-groups + pivots).
     * Absent = the flat grid every tenant has today. See lib/catalogGrouping.ts.
     */
    catalog?: CatalogBrandingConfig;
  };
  featureFlags: Record<string, boolean>;
  settings: Record<string, unknown>;
  provisioningStatus?: 'pending' | 'active' | 'failed';
  provisioningError?: string | null;
  domainAddedAt?: string | null;
  /** How the tenant was created: 'hub' (super-admin) or 'self_serve' (signup). */
  createdVia?: 'hub' | 'self_serve';
}

interface TenantContextType {
  tenant: Tenant | null;
  tenantId: string | null;
  isLoading: boolean;
  error: string | null;
  features: Record<FeatureKey, boolean>;
  refreshTenant: () => Promise<void>;
}

// Exported so a dev-only harness (pages/dev/CreatePreview.tsx) can nest a fake
// provider without the real DB-backed TenantProvider. App code must still read
// tenant state via `useTenant()` — never consume this context directly.
export const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTenant();
  }, []);

  const loadTenant = async () => {
    try {
      const slug = getTenantSlug();

      const { data, error: fetchError } = await supabase
        .from('tenants')
        .select('*')
        .eq('slug', slug)
        .single();

      if (fetchError) {
        // If tenant not found, fall back to thinkshow (platform owner)
        if (fetchError.code === 'PGRST116' && slug !== 'thinkshow') {
          console.warn(`Tenant "${slug}" not found, falling back to thinkshow`);
          const { data: defaultData, error: defaultError } = await supabase
            .from('tenants')
            .select('*')
            .eq('slug', 'thinkshow')
            .single();

          if (defaultError) throw defaultError;
          setTenantFromData(defaultData);
        } else {
          throw fetchError;
        }
      } else {
        setTenantFromData(data);
      }
    } catch (err: any) {
      console.error('Failed to load tenant:', err);
      setError(err.message || 'Failed to load tenant configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const setTenantFromData = (data: any) => {
    setTenant({
      id: data.id,
      name: data.name,
      slug: data.slug,
      logoUrl: data.logo_url,
      logoUrlLight: data.logo_url_light ?? null,
      isPublished: data.is_published ?? false,
      branding: data.branding || {},
      featureFlags: data.feature_flags || {},
      settings: data.settings || {},
      provisioningStatus: data.provisioning_status,
      provisioningError: data.provisioning_error,
      domainAddedAt: data.domain_added_at,
    });
  };

  const features = useMemo(() => getFeatures(tenant?.featureFlags), [tenant?.featureFlags]);

  const value = useMemo(() => ({
    tenant,
    tenantId: tenant?.id || null,
    isLoading,
    error,
    features,
    refreshTenant: loadTenant,
  }), [tenant, isLoading, error, features]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * Pick the right tenant logo for the surface you're rendering on.
 *
 * - `'light'` — light background (e.g. the student shell). Prefers `logoUrlLight`,
 *   falls back to `logoUrl`, then the Kula default.
 * - `'default'` (or omitted) — uses `logoUrl` directly. For dark / branded surfaces.
 */
export function pickTenantLogo(
  tenant: Pick<Tenant, 'logoUrl' | 'logoUrlLight'> | null | undefined,
  surface: 'light' | 'default' = 'default',
): string {
  if (surface === 'light' && tenant?.logoUrlLight) return tenant.logoUrlLight;
  return tenant?.logoUrl || '/kula-logo.svg';
}

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

/** Safe variant for components that can render outside the provider (previews). */
export const useOptionalTenant = (): TenantContextType | null => {
  return useContext(TenantContext) ?? null;
};

const MARKETING_THEME_IDS: ReadonlyArray<MarketingThemeId> = [
  'editorial',
  'warm',
  'utility',
  'revise',
];

/**
 * Resolved marketing theme for the current tenant, with default.
 *
 * In DEV only, `?marketingTheme=<id>` overrides it. Working on a theme
 * otherwise means writing it into a real tenant's branding to see it, which is
 * a bad trade for a preview — especially for the allowlisted themes, where the
 * only tenant that can select one may be in production. The guard is
 * `import.meta.env.DEV`, so the parameter does nothing in a built app.
 */
export const useMarketingTheme = (): MarketingThemeId => {
  const { tenant } = useTenant();
  const themed = tenant?.branding.marketing?.theme ?? DEFAULT_MARKETING_THEME;

  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const requested = new URLSearchParams(window.location.search).get('marketingTheme');
    if (requested && (MARKETING_THEME_IDS as string[]).includes(requested)) {
      return requested as MarketingThemeId;
    }
  }
  return themed;
};
